import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const moeLayerComponent: ComponentDef = {
  id: 'moe-layer',
  name: 'MoE Layer',
  description: 'Mixture-of-Experts: sparse routing sends each token to top-k experts. Supports shared-expert (DeepSeek), three routing strategies, and Standard FFN or SwiGLU expert types.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'd_ff', label: 'FFN dimension', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'n_experts', label: 'Routed experts', type: 'number', default: 4, min: 2, max: 128, step: 1 },
    { name: 'top_k', label: 'Top-K', type: 'number', default: 2, min: 1, max: 8, step: 1 },
    { name: 'shared_experts', label: 'Shared experts (0 = disabled)', type: 'number', default: 1, min: 0, max: 4, step: 1 },
    {
      name: 'routing',
      label: 'Routing strategy',
      type: 'select',
      default: 'token-choice',
      options: [
        { label: 'Token Choice (top-k)', value: 'token-choice' },
        { label: 'Expert Choice', value: 'expert-choice' },
        { label: 'Switch (top-1)', value: 'switch' }
      ]
    },
    {
      name: 'expert_type',
      label: 'Expert type',
      type: 'select',
      default: 'standard-ffn',
      options: [
        { label: 'Standard FFN (W1 -> act -> W2)', value: 'standard-ffn' },
        { label: 'SwiGLU Expert (LLaMA-style gated FFN)', value: 'swiglu' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const d_ff = Number(params.d_ff);
    const seq_len = Number(params.seq_len);
    const n_experts = Number(params.n_experts);
    const top_k = Number(params.top_k);
    const shared_experts = Number(params.shared_experts);
    const routing = String(params.routing || 'token-choice');
    const expert_type = String(params.expert_type || 'standard-ffn');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);
    g.norm('norm', 'x', 'LayerNorm');

    // -- Routing --
    g.linear('gate_logits', 'norm', [d_model, n_experts]);

    if (routing === 'switch') {
      g.softmax('gate_probs', 'gate_logits', -1);
      g.router('topk_gate', 'gate_probs', [seq_len, 1]);
      g.scale('load_balance_loss', 'gate_probs', 'aux_loss = f*p (load balance)');
    } else if (routing === 'expert-choice') {
      g.softmax('gate_probs', 'gate_logits', -1);
      g.transpose('gate_t', 'gate_probs', [n_experts, seq_len], 'T');
      g.router('expert_select', 'gate_t', [n_experts, top_k]);
    } else {
      g.softmax('gate_probs', 'gate_logits', -1);
      g.router('topk_gate', 'gate_probs', [seq_len, top_k]);
    }

    // -- Routed experts --
    const expertIds: string[] = [];
    for (let i = 0; i < n_experts; i++) {
      if (expert_type === 'swiglu') {
        // SwiGLU Expert (LLaMA-style gated FFN per expert):
        // gate_proj -> SiLU, up_proj -> linear, element-wise multiply, down_proj
        const gateProj = `expert_${i}_gate`;
        const upProj = `expert_${i}_up`;
        const gateAct = `expert_${i}_gate_act`;
        const gated = `expert_${i}_gated`;
        const downProj = `expert_${i}_down`;

        g.linear(gateProj, 'norm', [d_model, d_ff]);
        g.linear(upProj, 'norm', [d_model, d_ff]);
        g.activation(gateAct, gateProj, 'SiLU');
        g.mul(gated, gateAct, upProj);
        g.linear(downProj, gated, [d_ff, d_model]);
        expertIds.push(downProj);
      } else {
        // Standard FFN: W1 -> activation -> W2
        const up = `expert_${i}_up`;
        const act = `expert_${i}_act`;
        const down = `expert_${i}_down`;
        g.linear(up, 'norm', [d_model, d_ff]);
        g.activation(act, up, 'GELU');
        g.linear(down, act, [d_ff, d_model]);
        expertIds.push(down);
      }
    }

    let routedSum = expertIds[0];
    for (let i = 1; i < expertIds.length; i++) {
      const next = i === expertIds.length - 1 ? 'routed_out' : `sum_${i}`;
      g.add(next, routedSum, expertIds[i]);
      routedSum = next;
    }

    // -- Shared expert path (DeepSeek-V2/V3 style) --
    let finalOut = routedSum;
    if (shared_experts > 0) {
      // Shared expert always receives all tokens -- bypasses router
      if (expert_type === 'swiglu') {
        g.linear('shared_gate', 'norm', [d_model, d_ff * shared_experts]);
        g.linear('shared_up', 'norm', [d_model, d_ff * shared_experts]);
        g.activation('shared_gate_act', 'shared_gate', 'SiLU');
        g.mul('shared_gated', 'shared_gate_act', 'shared_up');
        g.linear('shared_down', 'shared_gated', [d_ff * shared_experts, d_model]);
      } else {
        g.linear('shared_up', 'norm', [d_model, d_ff * shared_experts]);
        g.activation('shared_act', 'shared_up', 'GELU');
        g.linear('shared_down', 'shared_act', [d_ff * shared_experts, d_model]);
      }
      g.add('combined', routedSum, 'shared_down');
      finalOut = 'combined';
    }

    g.add('out_add', 'x', finalOut);
    g.output('out', 'out_add');
    return g.build();
  }
};
